import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Lock, Trophy } from "lucide-react";
import HOFCelebration from "../components/HOFCelebration";
import HOFAmbient from "../components/HOFAmbient";
import HOFUnlockBurst from "../components/HOFUnlockBurst";

const CLASS_ORDER = ["heavyweight", "middleweight", "cruiserweight", "tag_team", "wildcard"];
const CLASS_LABEL = {
  heavyweight: "HEAVYWEIGHT", middleweight: "MIDDLEWEIGHT", cruiserweight: "CRUISERWEIGHT",
  tag_team: "TAG TEAM", wildcard: "WILDCARD",
};
const PEDESTAL_COLOR = {
  heavyweight:   "#FF007F",
  middleweight:  "#00F0FF",
  cruiserweight: "#FFD700",
  tag_team:      "#39FF14",
  wildcard:      "#9D00FF",
};
const STATUE_IMG = {
  heavyweight:   "/hof/Statue%20Heavyweight.webp",
  middleweight:  "/hof/Statue%20Middleweight.webp",
  cruiserweight: "/hof/Statue%20Cruiserweight.webp",
  tag_team:      "/hof/Statue%20Tag%20Team.webp",
  wildcard:      "/hof/Statue%20Wildcard.webp",
};
/* HOF Background image intrinsic size — used to lock statue positions to pedestals. */
const BG_W = 1920;
const BG_H = 988;
/* Pedestal centers (x) + top surface y, measured in native image pixels (1920x988).
   Derived from direct color-mask scan at y=720 in HOF Background.webp. */
const PEDESTAL_PX = [
  { x: 505,  y: 670 }, // pink   → heavyweight
  { x: 692,  y: 645 }, // blue   → middleweight
  { x: 874,  y: 635 }, // yellow → cruiserweight
  { x: 1071, y: 640 }, // green  → tag_team
  { x: 1255, y: 647 }, // purple → wildcard
  { x: 1452, y: 680 }, // peach  → reserved
];
/* Convert to %-of-image coords (stable regardless of rendered size). */
const PEDESTAL_X_PCT = PEDESTAL_PX.map(p => (p.x / BG_W) * 100);
const PEDESTAL_Y_PCT = PEDESTAL_PX.map(p => (p.y / BG_H) * 100);

export default function HOFPage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topOffset, setTopOffset] = useState(0);
  const [pendingLevelUps, setPendingLevelUps] = useState(null);
  const containerRef = useRef(null);
  const [stage, setStage] = useState({ w: 0, h: 0, left: 0, top: 0 });
  const [unlockPrompt, setUnlockPrompt] = useState(null); // { cls, cost }
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockBurstCls, setUnlockBurstCls] = useState(null); // class id to render burst on

  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('[data-testid="top-menu"]');
      if (el) setTopOffset(Math.max(0, Math.round(el.getBoundingClientRect().bottom)));
    };
    measure();
    const t1 = setTimeout(measure, 100);
    const t2 = setTimeout(measure, 400);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", measure); };
  }, []);

  // Measure the container and compute the actual rendered background rect under `background-size: cover`.
  // Statue overlays are positioned as % of this rect so they always lock onto the pedestals.
  useEffect(() => {
    const measureStage = () => {
      const el = containerRef.current;
      if (!el) return;
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (!cw || !ch) return;
      const scale = Math.max(cw / BG_W, ch / BG_H); // cover
      const w = BG_W * scale;
      const h = BG_H * scale;
      const left = (cw - w) / 2;
      const top = (ch - h) / 2;
      setStage({ w, h, left, top });
    };
    measureStage();
    const t = setTimeout(measureStage, 60);
    window.addEventListener("resize", measureStage);
    return () => { clearTimeout(t); window.removeEventListener("resize", measureStage); };
  }, [topOffset, loading]);

  const load = async () => {
    const r = await api.get("/hof/state");
    setState(r.data);
    setLoading(false);
    const pending = r.data?.level_up_pending || {};
    const hasPending = Object.values(pending).some((v) => Number(v) > 0);
    if (hasPending) {
      setPendingLevelUps(pending);
      // Ack on the server right away — the celebration component controls display.
      try {
        await api.post("/hof/ack-notifications");
        await refresh();
      } catch (_) { /* non-fatal */ }
    }
  };
  useEffect(() => { load(); }, []);

  const selectStatue = async (cls) => {
    // Determine next selection list based on induction_cap and current selection.
    const cap = Number(state?.induction_cap || 1);
    const currentList = state?.selected_statues || (state?.selected ? [state.selected] : []);
    let newList;
    if (cap >= 2) {
      // Toggle in/out, up to 2.
      if (currentList.includes(cls)) {
        newList = currentList.filter(c => c !== cls);
        if (newList.length === 0) newList = [cls];  // never allow zero
      } else if (currentList.length < cap) {
        newList = [...currentList, cls];
      } else {
        // Replace the oldest.
        newList = [currentList[currentList.length - 1], cls];
      }
    } else {
      newList = [cls];
    }
    try {
      await api.post("/hof/select", { class_ids: newList });
      await load();
      await refresh();
      if (newList.length > 1) {
        toast.success(`${newList.map(c => CLASS_LABEL[c]).join(" + ")} inducted`);
      } else {
        toast.success(`${CLASS_LABEL[cls]} statue selected`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const unlockStatue = (cls, cost) => {
    const handshakes = Number(user?.handshakes || 0);
    if (handshakes < cost) {
      toast.error(`Need ${cost} Handshakes (you have ${handshakes})`);
      return;
    }
    setUnlockPrompt({ cls, cost });
  };

  const confirmUnlock = async () => {
    if (!unlockPrompt) return;
    const { cls } = unlockPrompt;
    setUnlockBusy(true);
    try {
      await api.post(`/handshake-shop/hof-statue/${cls}`);
      await load();
      await refresh();
      toast.success(`${CLASS_LABEL[cls]} statue unlocked!`);
      setUnlockPrompt(null);
      // Trigger the pedestal-local burst so the player sees a celebratory flourish.
      setUnlockBurstCls(cls);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    } finally {
      setUnlockBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen p-8 text-center font-heading neon-cyan bg-[#0A051A]">OPENING HALL...</div>;
  if (!state?.unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center" style={{ background: "#0A051A" }}>
        <div className="card-surface p-8 max-w-md" style={{ borderColor: "#FFD700" }}>
          <Trophy size={48} className="mx-auto mb-3 text-[#FFD700]" style={{ filter: "drop-shadow(0 0 8px #FFD700)" }} />
          <h2 className="font-heading text-3xl neon-yellow mb-2">HALL OF FAME LOCKED</h2>
          <p className="text-[#A89BCC] font-mono text-sm mb-5">Beat Bulk Hogan on training stage 50 to unlock the Hall of Fame.</p>
          <button onClick={() => nav("/roster")} className="btn-neon px-5 py-2.5">BACK TO ROSTER</button>
        </div>
      </div>
    );
  }

  const statueMap = Object.fromEntries(state.statues.map(s => [s.class_id, s]));

  // Statue render height ≈ 26% of image height (matches HOF Statue Mockup proportions).
  const STATUE_H_PCT = 26;
  const statueH = (stage.h * STATUE_H_PCT) / 100;

  // During a level-up celebration, pick the first leveled-up statue and use its
  // pedestal center as the zoom origin → the stage pulls in on that statue
  // briefly, then returns to its default framing.
  const firstLeveledIdx = (() => {
    if (!pendingLevelUps) return -1;
    for (const [cls, cnt] of Object.entries(pendingLevelUps)) {
      if (Number(cnt) > 0) {
        const i = CLASS_ORDER.indexOf(cls);
        if (i >= 0) return i;
      }
    }
    return -1;
  })();
  const zoomActive = firstLeveledIdx >= 0;
  // Transform-origin — anchor the zoom to the statue's body (above pedestal top).
  const zoomOriginX = zoomActive ? PEDESTAL_X_PCT[firstLeveledIdx] : 50;
  const zoomOriginY = zoomActive
    ? PEDESTAL_Y_PCT[firstLeveledIdx] - STATUE_H_PCT * 0.5
    : 50;

  return (
    <div
      ref={containerRef}
      className="fixed left-0 right-0 bottom-0 overflow-hidden"
      style={{
        top: `${topOffset}px`,
        backgroundImage: "url(/hof/HOF%20Background.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#0A051A",
      }}
      data-testid="hof-page"
    >
      {/* subtle depth tint */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A051A]/40 via-transparent to-[#0A051A]/60 pointer-events-none z-[1]" />

      {/* Ambient spotlights + fading dust dots */}
      <HOFAmbient />

      {/* Level-up celebration overlay (triggers only when pending) */}
      <HOFCelebration
        pending={pendingLevelUps}
        onComplete={() => setPendingLevelUps(null)}
      />

      {/* Back button */}
      <div className="absolute top-3 left-3 z-20">
        <button
          data-testid="hof-back-btn"
          onClick={() => nav("/pvp")}
          className="flex items-center gap-2 text-[#A89BCC] hover:text-[#FFD700] font-mono text-[11px] uppercase tracking-widest bg-[#0A051AAA] px-3 py-1.5 rounded-md border border-[#39194D]"
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      {/* Image-locked stage — statues/labels inside this box are positioned
          as % of the image's actual rendered rect, which matches the background. */}
      <div
        className={`absolute z-10 pointer-events-none ${zoomActive ? "hof-stage-zoom" : ""}`}
        style={{
          left: `${stage.left}px`,
          top: `${stage.top}px`,
          width: `${stage.w}px`,
          height: `${stage.h}px`,
          transformOrigin: `${zoomOriginX}% ${zoomOriginY}%`,
          willChange: zoomActive ? "transform" : undefined,
        }}
        aria-hidden="true"
      >
        {CLASS_ORDER.map((cls, i) => {
          const st = statueMap[cls];
          const color = PEDESTAL_COLOR[cls];
          const selectedList = state.selected_statues || (state.selected ? [state.selected] : []);
          const isSelected = selectedList.includes(cls);
          const px = PEDESTAL_X_PCT[i];
          const py = PEDESTAL_Y_PCT[i];
          return (
            <React.Fragment key={cls}>
              {/* Statue — anchored so its BOTTOM touches the pedestal top */}
              <div
                className="absolute pointer-events-auto"
                style={{ left: `${px}%`, top: `${py}%`, transform: "translate(-50%, -100%)" }}
                data-testid={`hof-slot-${cls}`}
              >
                {st.unlocked ? (
                  <button
                    data-testid={`hof-select-${cls}`}
                    onClick={() => selectStatue(cls)}
                    className="relative transition-transform hover:scale-[1.06] block"
                    style={{
                      filter: isSelected
                        ? `drop-shadow(0 0 18px ${color}) drop-shadow(0 0 36px ${color}AA)`
                        : `drop-shadow(0 0 6px ${color}66)`,
                      animation: isSelected ? "hof-selected-pulse 2s ease-in-out infinite" : undefined,
                    }}
                  >
                    <img
                      src={STATUE_IMG[cls]}
                      alt={CLASS_LABEL[cls]}
                      style={{ height: `${statueH}px`, width: "auto", maxWidth: "none" }}
                      className="object-contain block"
                    />
                    {isSelected && (
                      <span
                        className="absolute -top-5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full font-heading text-[9px] uppercase tracking-widest whitespace-nowrap"
                        style={{ background: color, color: "#0A051A", boxShadow: `0 0 10px ${color}` }}
                      >
                        SELECTED
                      </span>
                    )}
                  </button>
                ) : (
                  <button
                    data-testid={`hof-unlock-${cls}`}
                    onClick={() => unlockStatue(cls, st.handshake_cost)}
                    title={`Unlock ${CLASS_LABEL[cls]} statue for ${st.handshake_cost} Handshakes`}
                    className="relative flex items-end justify-center transition-transform hover:scale-[1.08] cursor-pointer"
                    style={{ height: `${statueH * 0.45}px`, width: `${statueH * 0.4}px`, background: "transparent", border: "none", padding: 0 }}
                  >
                    <Lock size={Math.max(22, statueH * 0.22)} style={{ color: color + "DD", filter: `drop-shadow(0 0 10px ${color})` }} />
                  </button>
                )}
              </div>

              {/* Label chip + progress — sits on the pedestal face just below its top */}
              <div
                className="absolute flex flex-col items-center"
                style={{
                  left: `${px}%`,
                  top: `calc(${py}% + ${Math.max(6, stage.h * 0.012)}px)`,
                  transform: "translateX(-50%)",
                  width: `${Math.max(80, stage.w * 0.095)}px`,
                  pointerEvents: st.unlocked ? "none" : "auto",
                }}
              >
                <div
                  className="px-1.5 py-0.5 rounded text-[9px] font-heading uppercase tracking-widest border whitespace-nowrap"
                  style={{
                    background: "#0A051AEE",
                    color: st.unlocked ? color : "#695D8E",
                    borderColor: color + "88",
                    textShadow: `0 0 6px ${color}`,
                  }}
                >
                  {CLASS_LABEL[cls]}
                </div>
                {st.unlocked ? (
                  <>
                    <div className="text-[10px] font-heading mt-0.5" style={{ color }}>Lv {st.level}</div>
                    <div className="w-full h-1.5 rounded-full bg-[#0A051A] overflow-hidden border mt-0.5"
                         style={{ borderColor: color + "55" }}>
                      <div className="h-full transition-all"
                           style={{ width: `${st.progress_pct.toFixed(1)}%`, background: `linear-gradient(90deg, ${color}, #FFD700)`, boxShadow: `0 0 8px ${color}88` }} />
                    </div>
                    <div className="text-[8px] font-mono text-[#A89BCC] mt-0.5">
                      {st.progress.toLocaleString()}/{st.next_level_cost.toLocaleString()}
                    </div>
                  </>
                ) : (
                  <button
                    data-testid={`hof-unlock-chip-${cls}`}
                    onClick={() => unlockStatue(cls, st.handshake_cost)}
                    className="mt-1 text-[10px] font-mono text-[#FFD700] px-2 py-0.5 rounded border border-[#FFD70088] bg-[#0A051AEE] hover:bg-[#FFD70011] hover:text-white transition-colors font-heading uppercase tracking-widest whitespace-nowrap"
                    title={`Unlock for ${st.handshake_cost} Handshakes`}
                  >
                    🤝 {st.handshake_cost} · Unlock
                  </button>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {/* Unlock-flourish burst (confetti + glow ring) anchored over the pedestal of the just-unlocked statue */}
        {unlockBurstCls && (() => {
          const i = CLASS_ORDER.indexOf(unlockBurstCls);
          if (i < 0) return null;
          return (
            <div
              className="absolute pointer-events-none"
              style={{
                left: `${PEDESTAL_X_PCT[i]}%`,
                top: `${PEDESTAL_Y_PCT[i]}%`,
                // Anchor burst a bit above pedestal top (over the statue's center of mass)
                transform: `translate(0, -${statueH * 0.45}px)`,
                zIndex: 20,
              }}
            >
              <HOFUnlockBurst
                color={PEDESTAL_COLOR[unlockBurstCls]}
                onEnd={() => setUnlockBurstCls(null)}
              />
            </div>
          );
        })()}

        {/* Reserved 6th pedestal — label only, no statue */}
        <div
          className="absolute flex flex-col items-center pointer-events-none opacity-70"
          style={{
            left: `${PEDESTAL_X_PCT[5]}%`,
            top: `calc(${PEDESTAL_Y_PCT[5]}% + ${Math.max(6, stage.h * 0.012)}px)`,
            transform: "translateX(-50%)",
            width: `${Math.max(80, stage.w * 0.095)}px`,
          }}
          data-testid="hof-slot-reserved"
        >
          <div className="px-1.5 py-0.5 rounded text-[9px] font-heading uppercase tracking-widest border whitespace-nowrap"
               style={{ background: "#0A051AEE", color: "#FF9500", borderColor: "#FF950088" }}>
            RESERVED
          </div>
          <div className="mt-0.5 text-[8px] font-mono text-[#FF9500]">COMING SOON</div>
        </div>
      </div>

      {/* Footer hint */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[3vh] z-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FFD70055] bg-[#0A051ACC] text-[10px] font-mono text-[#A89BCC]">
          <Trophy size={12} className="text-[#FFD700]" />
          Select a statue — new HOF Points earned go to the chosen statue. +1 Power/Workrate/Fortune per level to its class.
        </div>
      </div>

      {/* In-game unlock confirmation modal */}
      {unlockPrompt && (() => {
        const { cls, cost } = unlockPrompt;
        const color = PEDESTAL_COLOR[cls] || "#FFD700";
        const handshakes = Number(user?.handshakes || 0);
        const after = handshakes - cost;
        return (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A051A]/85 backdrop-blur-sm"
            onClick={() => !unlockBusy && setUnlockPrompt(null)}
            data-testid="hof-unlock-modal"
          >
            <div
              className="relative w-full max-w-sm rounded-xl overflow-hidden hof-unlock-pop"
              style={{
                background: "linear-gradient(160deg, #140827, #1F0D3A)",
                border: `2px solid ${color}`,
                boxShadow: `0 0 32px ${color}66, 0 0 64px ${color}33, inset 0 0 24px ${color}22`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top ribbon */}
              <div
                className="px-4 py-2 font-heading uppercase tracking-widest text-[11px] text-center"
                style={{ background: `${color}22`, color, borderBottom: `1px solid ${color}66`, textShadow: `0 0 8px ${color}` }}
              >
                Hall of Fame · Unlock Statue
              </div>

              {/* Statue preview */}
              <div className="relative px-6 pt-5 pb-3 flex items-center justify-center">
                <div className="absolute inset-x-6 top-5 h-24 rounded-full blur-2xl pointer-events-none"
                     style={{ background: `radial-gradient(ellipse at center, ${color}66, transparent 70%)` }} />
                <img
                  src={STATUE_IMG[cls]}
                  alt={CLASS_LABEL[cls]}
                  className="relative h-32 w-auto object-contain"
                  style={{ filter: `drop-shadow(0 0 14px ${color}) drop-shadow(0 0 28px ${color}88) grayscale(1) brightness(0.95)` }}
                />
              </div>

              {/* Class title */}
              <div className="text-center px-4 pb-1">
                <div
                  className="font-heading text-2xl uppercase tracking-widest"
                  style={{ color, textShadow: `0 0 10px ${color}` }}
                >
                  {CLASS_LABEL[cls]}
                </div>
                <div className="text-[11px] font-mono text-[#A89BCC] mt-1">
                  Add this statue to your Hall of Fame. Level it with HOF Points to permanently buff all {CLASS_LABEL[cls].toLowerCase()} wrestlers.
                </div>
              </div>

              {/* Price breakdown */}
              <div className="mx-5 my-4 rounded-lg border border-[#FFD70044] bg-[#0A051A99] p-3">
                <div className="flex items-center justify-between text-[11px] font-mono mb-1.5">
                  <span className="text-[#A89BCC] uppercase tracking-widest">Balance</span>
                  <span className="text-[#FFD700] font-heading">🤝 {handshakes}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] font-mono mb-1.5">
                  <span className="text-[#A89BCC] uppercase tracking-widest">Cost</span>
                  <span className="text-[#FF007F] font-heading">− {cost}</span>
                </div>
                <div className="h-px my-1.5 bg-[#FFD70033]" />
                <div className="flex items-center justify-between text-[12px] font-mono">
                  <span className="text-[#A89BCC] uppercase tracking-widest">After</span>
                  <span className="font-heading" style={{ color: after >= 0 ? "#39FF14" : "#FF007F" }}>🤝 {after}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 px-5 pb-5">
                <button
                  data-testid="hof-unlock-cancel"
                  onClick={() => setUnlockPrompt(null)}
                  disabled={unlockBusy}
                  className="flex-1 px-3 py-2.5 rounded-md border border-[#39194D] text-[#A89BCC] hover:text-white font-heading uppercase tracking-widest text-xs transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  data-testid="hof-unlock-confirm"
                  onClick={confirmUnlock}
                  disabled={unlockBusy}
                  className="flex-[1.6] px-3 py-2.5 rounded-md font-heading uppercase tracking-widest text-xs transition-all hover:scale-[1.03] disabled:opacity-60 disabled:cursor-wait"
                  style={{
                    background: `linear-gradient(135deg, ${color}, #FFD700)`,
                    color: "#0A051A",
                    boxShadow: `0 0 16px ${color}AA`,
                    textShadow: "0 0 4px #FFFFFF55",
                  }}
                >
                  {unlockBusy ? "Unlocking…" : `Unlock · 🤝 ${cost}`}
                </button>
              </div>

              {/* Close X */}
              <button
                data-testid="hof-unlock-close"
                onClick={() => !unlockBusy && setUnlockPrompt(null)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-[#A89BCC] hover:text-white hover:bg-[#FFFFFF11] transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>
        );
      })()}

      <style>{`
        @keyframes hof-selected-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.04); }
        }
        @keyframes hof-stage-zoom {
          0%   { transform: scale(1); }
          20%  { transform: scale(1.22); }
          70%  { transform: scale(1.22); }
          100% { transform: scale(1); }
        }
        @keyframes hof-unlock-pop {
          0%   { opacity: 0; transform: scale(0.86) translateY(10px); filter: blur(6px); }
          55%  { opacity: 1; transform: scale(1.04) translateY(0);    filter: blur(0); }
          100% { opacity: 1; transform: scale(1)    translateY(0);    filter: blur(0); }
        }
        .hof-unlock-pop { animation: hof-unlock-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; will-change: transform, opacity, filter; }
        .hof-stage-zoom {
          animation: hof-stage-zoom 3.6s cubic-bezier(0.33, 0.1, 0.33, 1) forwards;
        }
      `}</style>
    </div>
  );
}
